//ClauseManagement.jsx
import React, { useEffect, useState } from 'react';
import { Modal, Table, Button, Space, Input, AutoComplete, Tooltip, Alert } from 'antd';
import { fetchClauses, deleteClause, fetchAlerts, updateAlert, promoteClauseToProd } from "../functions/alertClauseService";
import useFetchDashboardStructure from "../functions/FetchDashboardStructure";
import ClauseEditor from './ClauseEditor'; // Import the new ClauseEditor component
import AlertEditor from './AlertEditor'; // DAS-2266: Import AlertEditor for clickable alert IDs

const ClauseManagement = ({ isVisible, onClose }) => {
  const [clauses, setClauses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingClause, setEditingClause] = useState(null); // Track the clause being edited
  const { dashboardStructure: fetchedStructure } = useFetchDashboardStructure(isVisible, 'dashboard');
  const [dashboardStructure, setDashboardStructure] = useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchOptions, setSearchOptions] = useState([]);
  const [filteredClauses, setFilteredClauses] = useState([]);
  const [allAlerts, setAllAlerts] = useState([]); // DAS-2266: Store all alerts for editor
  const [editingAlert, setEditingAlert] = useState(null); // DAS-2266: Alert being edited

  // DAS-2277: Copy clause to PROD state
  const [copyToProdModalVisible, setCopyToProdModalVisible] = useState(false);
  const [copyToProdClauseId, setCopyToProdClauseId] = useState(null);
  const [copyToProdLoading, setCopyToProdLoading] = useState(false);
  const [copyToProdResult, setCopyToProdResult] = useState(null);


  useEffect(() => {
    if (isVisible) {
      // Set all rows to expanded by default when modal becomes visible
      setExpandedRowKeys(clauses.map((clause) => clause.ID));
    }
  }, [isVisible, clauses]);

  const handleExpand = (expanded, record) => {
    setExpandedRowKeys((prevKeys) =>
      expanded ? [...prevKeys, record.ID] : prevKeys.filter((key) => key !== record.ID)
    );
  };

  const toggleExpandCollapseAll = () => {
    if (expandedRowKeys.length === clauses.length) {
      setExpandedRowKeys([]); // Collapse all rows
    } else {
      setExpandedRowKeys(clauses.map((clause) => clause.ID)); // Expand all rows
    }
  };

  useEffect(() => {
    if (!dashboardStructure && fetchedStructure) {
      setDashboardStructure(fetchedStructure);
    }
  }, [fetchedStructure, dashboardStructure]);


    useEffect(() => {
    const loadClausesAndAlerts = async () => {
  setLoading(true);
  try {
    const [fetchedClauses, fetchedAlerts] = await Promise.all([
      fetchClauses(),
      fetchAlerts()
    ]);
    console.log("📦 fetchedAlerts sample:", fetchedAlerts.slice(0, 3));

    // console.log("fetched clauses in CLauseManagement ", fetchedClauses)
    console.log("fetched alerts in CLauseManagement ", fetchedAlerts)

    const clauseIdToAlertIds = {};
    for (const alert of fetchedAlerts) {
  const clauseList = typeof alert.clauses === 'string'
    ? alert.clauses.split(',').map(id => id.trim())
    : alert.clauses || [];

  for (const clauseId of clauseList) {
    const cid = String(clauseId);
    if (!clauseIdToAlertIds[cid]) {
      clauseIdToAlertIds[cid] = [];
    }
    clauseIdToAlertIds[cid].push(alert.ID);
  }
}



    const enrichedClauses = fetchedClauses.map(clause => ({
      ...clause,
      alerts: clauseIdToAlertIds[String(clause.ID)]?.map(id => {
  const alert = fetchedAlerts.find(a => a.ID === id);
  // DAS-2266: Include type/scope/subtype for tooltip display
  return {
    id,
    status: alert?.status,
    alert_type: alert?.alert_type,
    alert_subtype: alert?.alert_subtype,
    scope: alert?.scope
  };
}) || []
    }));

    console.log("🔄 Enriched clauses with alerts", enrichedClauses);
    setClauses(enrichedClauses);
    setAllAlerts(fetchedAlerts); // DAS-2266: Store for alert editor
    setFilteredClauses(enrichedClauses); // initialize filtered view too
  } catch (error) {
    console.error('Error fetching clauses or alerts:', error);
  } finally {
    setLoading(false);
  }
};


    if (isVisible) {
      loadClausesAndAlerts();
    }
  }, [isVisible]);

  
  const refreshClauses = async () => {
  try {
    const [fetchedClauses, fetchedAlerts] = await Promise.all([
      fetchClauses(),
      fetchAlerts()
    ]);

    const clauseIdToAlertIds = {};
    for (const alert of fetchedAlerts) {
  const clauseList = typeof alert.clauses === 'string'
    ? alert.clauses.split(',').map(id => id.trim())
    : alert.clauses || [];

  for (const clauseId of clauseList) {
    const cid = String(clauseId);
    if (!clauseIdToAlertIds[cid]) {
      clauseIdToAlertIds[cid] = [];
    }
    clauseIdToAlertIds[cid].push(alert.ID);
  }
}



    const enrichedClauses = fetchedClauses.map(clause => ({
      ...clause,
      alerts: clauseIdToAlertIds[String(clause.ID)]?.map(id => {
  const alert = fetchedAlerts.find(a => a.ID === id);
  // DAS-2266: Include type/scope/subtype for tooltip display
  return {
    id,
    status: alert?.status,
    alert_type: alert?.alert_type,
    alert_subtype: alert?.alert_subtype,
    scope: alert?.scope
  };
}) || []
    }));

    setClauses(enrichedClauses);
    setAllAlerts(fetchedAlerts); // DAS-2266: Store for alert editor
    setFilteredClauses(enrichedClauses);
  } catch (err) {
    console.error("Error refreshing clauses and alerts", err);
  }
};




  const columns = [
    { title: 'ID', dataIndex: 'ID', key: 'ID' },
    { title: 'Clause Field', dataIndex: 'clause_field', key: 'clause_field', render: (text, record) => `${text} (${record.clause_field_sourcetable})` },
    // Add Clause Field Sourcetable column
    { title: 'Clause Field Sourcetable', dataIndex: 'clause_field_sourcetable', key: 'clause_field_sourcetable' },



    { title: 'Clause Field Target', dataIndex: 'clause_field_target', key: 'clause_field_target' },
    { title: 'Clause Field Target Value', dataIndex: 'clause_field_target_value', key: 'clause_field_target_value' },
    { title: 'Operator', dataIndex: 'clause_operator', key: 'clause_operator' },
    { title: 'Threshold', dataIndex: 'threshold', key: 'threshold' },
    { title: 'Threshold Field', dataIndex: 'threshold_field', key: 'threshold_field', render: (text, record) => `${text} (${record.threshold_sourcetable})` },
    // Add Threshold Sourcetable column
    { title: 'Threshold Sourcetable', dataIndex: 'threshold_sourcetable', key: 'threshold_sourcetable' },
    // Add Threshold Field Target column
    { title: 'Threshold Field Target', dataIndex: 'threshold_field_target', key: 'threshold_field_target' },

    // Add Threshold Field Target Value column
    { title: 'Threshold Field Target Value', dataIndex: 'threshold_field_target_value', key: 'threshold_field_target_value' },

    {
      title: 'Action',
      key: 'action',
      render: (_, record) => {
  // console.log("⛏️ Action render for clause ID:", record.ID, "alerts:", record.alerts);

  return (
    <Space size="middle">
      <Button onClick={() => setEditingClause(record)}>Edit</Button>
      <Button size="small" onClick={() => handleCopyToProd(record.ID)}>Copy to PROD</Button>

{record.alerts && record.alerts.length > 0 ? (
  <span>
    Part of alerts{" "}
    {record.alerts.map((a, idx) => (
      <Tooltip
        key={a.id}
        title={`${a.scope || '?'} | ${a.alert_type || '?'} | ${a.alert_subtype || '?'}`}
      >
        <span
          style={{
            color: a.status === 0 ? 'red' : '#1890ff',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
          onClick={() => {
            const alertData = allAlerts.find(al => al.ID === a.id);
            if (alertData) setEditingAlert(alertData);
          }}
        >
          {a.id}
        </span>
        {idx < record.alerts.length - 1 ? ', ' : ''}
      </Tooltip>
    ))}
  </span>
) : (
  <Button danger onClick={() => handleDeleteClause(record.ID)}>Delete</Button>
)}


    </Space>
  );
}

    },
    
  ];

  const handleDeleteClause = async (clauseId) => {
    setLoading(true);
    try {
      await deleteClause(clauseId);
      setClauses(clauses.filter(clause => clause.ID !== clauseId));
    } catch (error) {
      console.error('Error deleting clause:', error);
    } finally {
      setLoading(false);
    }
  };

  // DAS-2277: Copy clause to PROD — open preview modal
  const handleCopyToProd = async (clauseId) => {
    setCopyToProdClauseId(clauseId);
    setCopyToProdResult(null);
    setCopyToProdLoading(true);
    setCopyToProdModalVisible(true);
    try {
      const preview = await promoteClauseToProd(clauseId, { commit: false });
      setCopyToProdResult(preview);
    } catch (err) {
      setCopyToProdResult({ ok: false, status: 'error', message: err.message || 'Failed to connect to PROD' });
    } finally {
      setCopyToProdLoading(false);
    }
  };

  // DAS-2277: Confirm and write clause to PROD
  const confirmCopyToProd = async () => {
    setCopyToProdLoading(true);
    try {
      const result = await promoteClauseToProd(copyToProdClauseId, { commit: true });
      setCopyToProdResult(result);
    } catch (err) {
      setCopyToProdResult({ ok: false, status: 'error', message: err.message || 'Write to PROD failed' });
    } finally {
      setCopyToProdLoading(false);
    }
  };

  const handleSearch = (value) => {
  setSearchInput(value);

  if (!value) {
  setSearchOptions([]);
  setFilteredClauses(clauses); // restore full list on clear
  return;
}


  const keyword = value.toLowerCase();
  const matched = clauses
    .filter(clause =>
      Object.values(clause).some(val =>
        typeof val === 'string' && val.toLowerCase().includes(keyword)
      )
    )
    .slice(0, 10) // limit suggestions

  const options = matched.map(c => ({
    value: `${c.ID} - ${c.clause_field}`,
    clauseId: c.ID
  }));

  setSearchOptions(options);
};

const handleSelect = (value, option) => {
  const selectedClauseId = option.clauseId;
  const clause = clauses.find(c => c.ID === selectedClauseId);

  if (clause) {
    setExpandedRowKeys([selectedClauseId]);
    setFilteredClauses([clause]);  // <-- show only selected clause in table
  }
};



  return (
    <>
      {/* // Add expandedRowRender to display clause_txt in an expanded row */}
      <Modal
      title="Manage Clauses"
      visible={isVisible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="ok" type="primary" onClick={onClose}>
          OK
        </Button>,
      ]}
      width={2000}
    >
      <Space style={{ marginBottom: 16 }}>
  <AutoComplete
    style={{ width: 400 }}
    options={searchOptions}
    onSearch={handleSearch}
    onSelect={handleSelect}
    placeholder="Search clause fields, targets, operators..."
    allowClear
  />
  <Button onClick={toggleExpandCollapseAll}>
    {expandedRowKeys.length === clauses.length ? 'Close All Clause Texts' : 'Open All Clause Texts'}
  </Button>
  <Button type="primary" onClick={() => setEditingClause({})}>
    Create New Clause
  </Button>
</Space>

      
      <Table
        dataSource={filteredClauses}
        // dataSource={clauses}
        size="small"
        columns={columns}
        rowKey="ID"
        loading={loading}
        expandable={{
          expandedRowRender: (record) => (
            <div style={{ padding: '8px 24px', fontStyle: 'italic', color: 'blue' }}> {/* Color clause_txt blue */}
              {record.clause_txt}
            </div>
          ),
          expandedRowKeys: expandedRowKeys, // Control expanded rows
          onExpand: handleExpand, // Handle row expansion/collapse
        }}
      />
    </Modal>


      {editingClause && (
        <ClauseEditor
          visible={!!editingClause}
          onClose={() => setEditingClause(null)}
          clause={editingClause}
          dashboardStructure={dashboardStructure}
          refreshClauses={refreshClauses}
        />
      )}

      {/* DAS-2266: AlertEditor for clickable alert IDs */}
      {editingAlert && (
        <AlertEditor
          visible={!!editingAlert}
          onClose={() => setEditingAlert(null)}
          alertData={editingAlert}
          onSave={async (updatedAlert) => {
            try {
              await updateAlert(updatedAlert);
              setEditingAlert(null);
              refreshClauses(); // Refresh to pick up any changes
            } catch (err) {
              console.error("Error saving alert from ClauseManagement:", err);
            }
          }}
          clauses={clauses}
          dashboardStructure={dashboardStructure}
          refreshAlerts={refreshClauses}
        />
      )}

      {/* DAS-2277: Copy Clause to PROD modal */}
      <Modal
        title={`Copy Clause #${copyToProdClauseId} to PROD`}
        open={copyToProdModalVisible}
        onCancel={() => setCopyToProdModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setCopyToProdModalVisible(false)}>
            Close
          </Button>,
          copyToProdResult?.status === 'preview' && (
            <Button
              key="confirm"
              type="primary"
              danger
              loading={copyToProdLoading}
              onClick={confirmCopyToProd}
            >
              Confirm &amp; Write to PROD
            </Button>
          ),
        ].filter(Boolean)}
      >
        {copyToProdLoading && !copyToProdResult && <p>Checking PROD...</p>}

        {copyToProdResult?.status === 'exists' && (
          <Alert
            type="warning"
            showIcon
            message="Clause already exists on PROD"
            description={
              <>
                <p>This clause already exists on PROD as <strong>ID {copyToProdResult.prod_clause_id}</strong>.</p>
                <p><strong>DEV clause_txt:</strong> {copyToProdResult.dev_clause_txt}</p>
                <p><strong>PROD clause_txt:</strong> {copyToProdResult.prod_clause_txt}</p>
                <p>No action needed.</p>
              </>
            }
          />
        )}

        {copyToProdResult?.status === 'preview' && (
          <Alert
            type="info"
            showIcon
            message="Ready to copy to PROD"
            description={
              <>
                <p>Clause DEV #{copyToProdClauseId} does <strong>not</strong> exist on PROD.</p>
                <p><strong>Fields to insert:</strong></p>
                <pre style={{ fontSize: 11, maxHeight: 300, overflow: 'auto' }}>
                  {JSON.stringify(copyToProdResult.clause_preview, null, 2)}
                </pre>
                <p style={{ color: 'red', fontWeight: 'bold' }}>Click "Confirm &amp; Write to PROD" to insert.</p>
              </>
            }
          />
        )}

        {copyToProdResult?.status === 'success' && (
          <Alert
            type="success"
            showIcon
            message="Clause copied to PROD"
            description={
              <p>
                DEV #{copyToProdClauseId} inserted into PROD as <strong>ID {copyToProdResult.prod_clause_id}</strong>.
              </p>
            }
          />
        )}

        {copyToProdResult?.status === 'error' && (
          <Alert
            type="error"
            showIcon
            message="Error"
            description={copyToProdResult.message}
          />
        )}
      </Modal>
    </>
  );
};

export default ClauseManagement;
